import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SEO, getBreadcrumbSchema } from '@/components/seo/SEO';
import { FloralCorner, PaperLayerBg } from '@/components/ui/FloralElements';
import { Shield, Mail, MapPin } from 'lucide-react';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

export const PrivacyPolicyPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Privacy Policy', path: '/privacy-policy' }
  ];

  return (
    <>
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for Little House on the Table. Learn how we collect, use, and protect your personal information when you visit our Nebraska family ranch website."
        path="/privacy-policy"
        structuredData={getBreadcrumbSchema(breadcrumbs)}
      />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-hero overflow-hidden">
        <FloralCorner position="top-right" className="opacity-30" />
        <FloralCorner position="bottom-left" className="opacity-30" />
        
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
              <Shield className="h-4 w-4" />
              Your Privacy Matters
            </div>
            <h1 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-6">
              Privacy Policy
            </h1>
            <p className="text-lg text-muted-foreground">
              Last updated: July 1, 2025
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <PaperLayerBg className="p-8 md:p-12">
              <div className="prose prose-lg max-w-none">
                <h2 className="font-serif text-2xl font-bold text-foreground mb-4">Introduction</h2>
                <p className="text-muted-foreground mb-6">
                  Little House on the Table (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;) is committed to protecting your privacy. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
                  when you visit our website littlehouseonthetable.com.
                </p>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">Information We Collect</h2>
                <p className="text-muted-foreground mb-4">
                  We may collect information about you in a variety of ways:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                  <li><strong>Personal Data:</strong> Name, email address, phone number, and mailing address that you voluntarily provide when contacting us or subscribing to our newsletter.</li>
                  <li><strong>Derivative Data:</strong> Information our servers automatically collect when you access our website, such as your IP address, browser type, operating system, access times, and pages viewed.</li>
                  <li><strong>Financial Data:</strong> All payment processing is handled securely through Square. We do not store your credit card information on our servers.</li>
                </ul>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">How We Use Your Information</h2>
                <p className="text-muted-foreground mb-4">
                  We use the information we collect to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                  <li>Respond to your inquiries and fulfill your requests</li>
                  <li>Send you newsletters and marketing communications (with your consent)</li>
                  <li>Improve our website and customer service</li>
                  <li>Process transactions through our Square online store</li>
                  <li>Comply with legal obligations</li>
                </ul>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">Cookies and Tracking</h2>
                <p className="text-muted-foreground mb-6">
                  We may use cookies and similar tracking technologies to collect information about your 
                  browsing activities. You can set your browser to refuse cookies, but some features of 
                  our website may not function properly without them.
                </p>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">Third-Party Services</h2>
                <p className="text-muted-foreground mb-6">
                  Our website may contain links to third-party websites and services, including our 
                  Square online store and Facebook page. We are not responsible for the privacy practices 
                  of these third parties. We encourage you to review their privacy policies.
                </p>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">Data Security</h2>
                <p className="text-muted-foreground mb-6">
                  We implement appropriate technical and organizational measures to protect your personal 
                  information. However, no method of transmission over the Internet is 100% secure, and 
                  we cannot guarantee absolute security.
                </p>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">Your Rights</h2>
                <p className="text-muted-foreground mb-4">
                  Depending on your location, you may have the right to:
                </p>
                <ul className="list-disc pl-6 text-muted-foreground mb-6 space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Opt-out of marketing communications</li>
                </ul>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">Children&apos;s Privacy</h2>
                <p className="text-muted-foreground mb-6">
                  Our website is not intended for children under 13 years of age. We do not knowingly 
                  collect personal information from children under 13.
                </p>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">Changes to This Policy</h2>
                <p className="text-muted-foreground mb-6">
                  We may update this Privacy Policy from time to time. The updated version will be 
                  indicated by an updated &quot;Last updated&quot; date at the top of this page.
                </p>

                <h2 className="font-serif text-2xl font-bold text-foreground mb-4 mt-8">Contact Us</h2>
                <p className="text-muted-foreground mb-4">
                  If you have questions about this Privacy Policy, please contact us:
                </p>
                <div className="bg-secondary/50 rounded-lg p-6 space-y-3">
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <Mail className="h-5 w-5 text-primary" />
                    <a href="mailto:wohlersbeef@gmail.com" className="hover:text-primary transition-colors">
                      wohlersbeef@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    <a href="tel:+13086655400" className="hover:text-primary transition-colors">
                      (308) 665-5400
                    </a>
                  </div>
                  <div className="flex items-start gap-3 text-muted-foreground">
                    <MapPin className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                    <span>Crawford, NE, United States</span>
                  </div>
                </div>
              </div>
            </PaperLayerBg>
          </div>
        </div>
      </section>
    </>
  );
};

export default PrivacyPolicyPage;